import React, { createContext, useContext, useState, useEffect } from 'react'
import AuthService from 'services/AuthService'

import { CartProduct, CartProductUpdate } from 'api/cart/interfaces'

import {
  ApiGetCart,
  ApiAddItemToCart,
  ApiClearCart,
  ApiUpdateCartItem,
  ApiRemoveFromCartItem
} from 'api/cart/cart'

type CartContextType = {
  cart: CartProduct[]
  cartCount: number
  isLoading: boolean
  addItem: (item: CartProductUpdate) => Promise<void>
  removeItem: (purchaseNo: string) => Promise<void>
  updateItemQuantity: (item: CartProductUpdate) => Promise<void>
  clearCart: () => Promise<void>
  reloadCart: () => Promise<void>
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<CartProduct[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { firstName, accessToken } = AuthService.loadCookies()
  const isLoggedIn = !!firstName && !!accessToken

  useEffect(() => {
    if (isLoggedIn) reloadCart()
  }, [isLoggedIn])

  const reloadCart = async () => {
    const data = await ApiGetCart()
    setCart(data)
    setIsLoading(false)
  }

  const addItem = async (item: CartProductUpdate) => {
    setIsLoading(true)
    const existingItem = cart.find(i => i.purchase_no === item.purchase_no)

    if (existingItem) {
      const newQuantity = existingItem.quantity + item.quantity

      if (newQuantity > existingItem.stock) {
        await ApiUpdateCartItem(existingItem.purchase_no, existingItem.stock)
      } else await ApiUpdateCartItem(existingItem.purchase_no, newQuantity)
    } else {
      await ApiAddItemToCart(item)
    }

    await reloadCart()
  }

  const updateItemQuantity = async (item: CartProductUpdate) => {
    setIsLoading(true)
    await ApiUpdateCartItem(item.purchase_no, item.quantity)
    await reloadCart()
  }

  const removeItem = async (purchaseNo: string) => {
    setIsLoading(true)
    await ApiRemoveFromCartItem(purchaseNo)
    await reloadCart()
  }

  const clearCart = async () => {
    setIsLoading(true)
    await ApiClearCart()
    setCart([])
    setIsLoading(false)
  }

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <CartContext.Provider
      value={{
        cart,
        isLoading,
        addItem,
        updateItemQuantity,
        removeItem,
        clearCart,
        cartCount,
        reloadCart
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) throw new Error('useCart must be used within a CartProvider')
  return context
}
